import type {
  SchemaFieldDefinition,
  SchemaFieldType,
  TaskActionFormField,
  TaskActionFormFieldType,
  TaskTypeAction,
} from '@erp/shared-types';

// Pure mapping between a task action's declared form fields and the shared
// DynamicSchemaForm's SchemaFieldDefinition shape — reusing the existing
// schema-driven form machinery instead of building a new renderer.

const FIELD_TYPE_MAP: Record<TaskActionFormFieldType, SchemaFieldType> = {
  text: 'string',
  textarea: 'text',
  number: 'float',
  boolean: 'boolean',
  select: 'enum',
  date: 'date',
};

/** Project declared action form fields into DynamicSchemaForm field defs. */
export function taskActionFieldsToSchemaFields(
  fields: readonly TaskActionFormField[],
): SchemaFieldDefinition[] {
  return fields.map((field, index) => ({
    id: field.key,
    fieldName: field.key,
    fieldType: FIELD_TYPE_MAP[field.type],
    label: field.label,
    description: field.helpText,
    isRequired: field.required,
    isMultiple: false,
    sortOrder: index,
    enumValues: field.options ?? null,
    uiConfig: { width: 'full' },
  }));
}

/**
 * Initial form values: required booleans start as an explicit `false` so the
 * strict server-side payload schema (which requires a boolean value) accepts
 * an untouched toggle.
 */
export function initialTaskActionValues(
  fields: readonly TaskActionFormField[],
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.type === 'boolean') values[field.key] = false;
  }
  return values;
}

/**
 * Project the DynamicSchemaForm's value bag into the submit payload: empty
 * strings and nulls for optional fields are dropped (the server schema
 * validates presence per declared field, strictly rejecting unknown keys).
 */
export function buildTaskActionPayload(
  fields: readonly TaskActionFormField[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    const value = values[field.key];
    if (value === undefined || value === null || value === '') continue;
    payload[field.key] = value;
  }
  return payload;
}

/** Client-side required check so the dialog can disable submit with a hint. */
export function missingRequiredActionFields(
  fields: readonly TaskActionFormField[],
  values: Record<string, unknown>,
): TaskActionFormField[] {
  return fields.filter((field) => {
    if (!field.required) return false;
    const value = values[field.key];
    if (field.type === 'boolean') return typeof value !== 'boolean';
    return value === undefined || value === null || value === '';
  });
}

/**
 * True when the task type declares custom actions. Those replace the default
 * Complete / Fail / Cancel controls; the task type editor steers admins to
 * include actions that close the task.
 */
export function taskTypeReplacesCloseControls(
  actions: readonly TaskTypeAction[] | null | undefined,
): boolean {
  return (actions ?? []).length > 0;
}
